/**
 * Cat Navigation Manager - CSS-First Approach
 */
const CatNavigationManager = (() => {
  
  const config = {
    selectors: {
      header: '.theme__header',
      collectionNavWrapper: '.js-collection-nav-wrapper',
      searchField: '#fs-search-field',
      searchFieldValue: '#search-field-value',
      announcementWrapper: '.announcement__wrapper',
      collectionTextContent: '.collection__text-content'
    },
    classes: {
      headerHidden: 'header-hidden',
      atTop: 'at-top',
      headerHiddenFallback: 'header-hidden-fallback',
      stickyActive: 'sticky-active'
    },
    cssProperties: {
      headerHeight: '--header-height',
      collectionNavWrapperHeight: '--collection-nav-wrapper-height',
      searchHeight: '--search-height',
      announcementHeight: '--announcement-height',
      stickyThreshold: '--sticky-threshold'
    },
    thresholds: {
      scroll: 50,
      top: 40  // Increased from 10px to 40px to account for announcement height
    }
  };
  
  let lastScrollY = 0;
  let isScrollingDown = false;
  let stickyThreshold = 0;
  let originalNavPosition = 0;
  let isInitialized = false;
  let hasUserScrolled = false;
  let resizeHandler = null;

  // iOS keyboard detection
  let isKeyboardOpen = false;
  let lastViewportHeight = window.innerHeight;
  let isIgnoringScroll = false;

  // DOM elements - initialized safely
  let $header, $collectionNavWrapper, $searchField, $searchFieldValue, $collectionTextContent;
  
  /**
   * Initialize DOM elements with proper validation
   */
  function initializeElements() {
    $header = document.querySelector(config.selectors.header);
    $collectionNavWrapper = document.querySelector(config.selectors.collectionNavWrapper);
    $searchField = document.querySelector(config.selectors.searchField);
    $searchFieldValue = document.querySelector(config.selectors.searchFieldValue);
    $collectionTextContent = document.querySelector(config.selectors.collectionTextContent);
    
    // Header is critical for navigation functionality
    if (!$header) {
      console.warn('CatNavigationManager: Header element not found - navigation disabled');
      return false;
    }
    
    return true;
  }

  // globals
  const isCollectionPage = document.body?.classList.contains('template-collection') ?? false;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;

  let ticking = false;

  /**
   * Safely set CSS custom property
   */
  function setCSSProperty(property, value) {
    document.documentElement?.style?.setProperty(property, value);
  }

  /**
   * Get element dimensions with null safety
   */
  function getElementHeight(element) {
    return element?.offsetHeight ?? 0;
  }

  function getElementRect(element) {
    if (!element || typeof element.getBoundingClientRect !== 'function') {
      return null;
    }
    return element.getBoundingClientRect();
  }

  /**
   * iOS Keyboard Detection
   */
  function setupKeyboardDetection() {
    if (!isMobile) return;

    // Track input focus events
    document.addEventListener('focusin', (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') {
        isIgnoringScroll = true;
        
        // Stop ignoring scroll after a delay if no keyboard is detected
        setTimeout(() => {
          if (!isKeyboardOpen) {
            isIgnoringScroll = false;
          }
        }, 300);
      }
    });

    document.addEventListener('focusout', (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') {
        // Delay resuming scroll handling to allow for keyboard close animation
        setTimeout(() => {
          isIgnoringScroll = false;
          isKeyboardOpen = false;
        }, 500);
      }
    });

    // Track viewport changes (iOS Safari keyboard) - only if API exists
    if (window.visualViewport?.addEventListener) {
      window.visualViewport.addEventListener('resize', () => {
        const currentHeight = window.visualViewport.height;
        const heightDifference = lastViewportHeight - currentHeight;

        // Keyboard likely opened if viewport height decreased significantly
        if (heightDifference > 150) {
          isKeyboardOpen = true;
          isIgnoringScroll = true;
        }
        // Keyboard likely closed if viewport height increased significantly
        else if (heightDifference < -150) {
          setTimeout(() => {
            isKeyboardOpen = false;
            isIgnoringScroll = false;
          }, 300);
        }

        lastViewportHeight = currentHeight;
      });
    } else {
      // Fallback for devices without visualViewport support
      window.addEventListener('resize', () => {
        const currentHeight = window.innerHeight;
        const heightDifference = lastViewportHeight - currentHeight;
        
        // Similar logic for window resize
        if (heightDifference > 150) {
          isKeyboardOpen = true;
          isIgnoringScroll = true;
        } else if (heightDifference < -150) {
          setTimeout(() => {
            isKeyboardOpen = false;
            isIgnoringScroll = false;
          }, 300);
        }

        lastViewportHeight = currentHeight;
      });
    }
  }

  /**
   * Calculate the sticky threshold and store original nav position
   */
  function calculateStickyThreshold() {
      stickyThreshold = 10;
   
      const imageNavigation = document.getElementById('image-navigation');
      if(imageNavigation) {
        stickyThreshold = stickyThreshold + getElementHeight(imageNavigation);
      }

      const collectionDescription = document.querySelector('.collection__text-content .rte');
      if(collectionDescription) {
        stickyThreshold = stickyThreshold + getElementHeight(collectionDescription);
      }

      const collectionHero = document.querySelector('.collection__hero');
      if(collectionHero) {
        stickyThreshold = stickyThreshold + getElementHeight(collectionHero);
      }

      setCSSProperty(config.cssProperties.stickyThreshold, `${stickyThreshold}px`);
      return;
  }

  /**
   * Measure original positions in static state
   */
  function measureOriginalPositions() {
    if (!document.body) return;

    if (isMobile) {
      // iOS SAFE APPROACH - No temporary scrolling to avoid iOS Safari issues
      const savedScrollY = window.scrollY;
      
      // If we haven't measured yet, use a safe calculation
      if (originalNavPosition === 0) {
        // Temporarily reset classes to get more accurate readings without scrolling
        const originalBodyClasses = document.body.className;
        document.body.classList.remove(config.classes.stickyActive);
        document.body.classList.remove(config.classes.headerHiddenFallback);
        document.body.classList.add(config.classes.atTop);
        
        // Force layout recalculation
        document.body.offsetHeight;
        
        if (isCollectionPage && $collectionNavWrapper) {
          // For collection pages, measure nav wrapper's current position and subtract scroll
          const navRect = getElementRect($collectionNavWrapper);
          originalNavPosition = (navRect?.bottom ?? 0) - savedScrollY;
        } else if (!isCollectionPage && $searchField) {
          // For search pages, measure search field's current position and subtract scroll
          const searchRect = getElementRect($searchField);
          originalNavPosition = (searchRect?.top ?? 0) - savedScrollY;
        } else if (!isCollectionPage && $collectionNavWrapper) {
          // Fallback: measure nav wrapper
          const navRect = getElementRect($collectionNavWrapper);
          originalNavPosition = (navRect?.bottom ?? 0) - savedScrollY;
        }
        
        // Ensure we have a reasonable minimum
        if (originalNavPosition < 50) {
          originalNavPosition = 80; // Safe default
        }
        
        // Restore original classes
        document.body.className = originalBodyClasses;
      }
      
      return;
    }

    // Temporarily reset classes to get natural positions
    const originalBodyClasses = document.body.className;
    document.body.classList.remove(config.classes.stickyActive);
    document.body.classList.remove(config.classes.headerHiddenFallback);
    document.body.classList.add(config.classes.atTop);

    // Force layout recalculation
    document.body.offsetHeight;

    const colTextContentRect = getElementRect($collectionTextContent);
    const headerRect = getElementRect($header);
    
    if (isCollectionPage && $collectionNavWrapper) {
      originalNavPosition = (colTextContentRect?.bottom ?? headerRect?.bottom ?? 0) + window.scrollY;
    } else if (!isCollectionPage && $searchField) {
      const searchRect = getElementRect($searchField);
      originalNavPosition = (searchRect?.top ?? 0) + window.scrollY;
    } else if (!isCollectionPage && $collectionNavWrapper) {
      const navRect = getElementRect($collectionNavWrapper);
      originalNavPosition = (navRect?.bottom ?? 0) + window.scrollY;
    }

    // Restore original classes
    document.body.className = originalBodyClasses;
  }

  /**
   * Update CSS custom properties with actual element heights
   */
  function updateHeights() {
    // Set header height
    setCSSProperty(config.cssProperties.headerHeight, `${getElementHeight($header)}px`);
    
    // Set collection nav wrapper height
    setCSSProperty(config.cssProperties.collectionNavWrapperHeight, `${getElementHeight($collectionNavWrapper)}px`);
    
    // Calculate and set search height
    if ($searchField && $searchFieldValue) {
      const searchFieldStyle = getComputedStyle($searchField);
      const searchFieldValueStyle = getComputedStyle($searchFieldValue);
      const marginBottom = parseInt(searchFieldValueStyle.getPropertyValue('margin-bottom')) || 0;
      const paddingTop = parseInt(searchFieldStyle.getPropertyValue('padding-top')) || 0;
      const searchHeight = getElementRect($searchFieldValue)?.height ?? 0;
      setCSSProperty(config.cssProperties.searchHeight, `${searchHeight + marginBottom + paddingTop}px`);
    } else {
      setCSSProperty(config.cssProperties.searchHeight, '0px');
    }
    
    // Set announcement height
    // Use the announcement wrapper that is actually visible and has height
    const desktopAnnouncement = document.querySelector('.announcement__wrapper.desktop');
    const mobileAnnouncement = document.querySelector('.announcement__wrapper.mobile');
    
    let activeAnnouncementHeight = 0;
    
    // Check which announcement wrapper is actually visible (not hidden by transforms)
    const checkIfVisible = (element) => {
      if (!element) return false;
      const computedStyle = getComputedStyle(element);
      const isHidden = computedStyle.opacity === '0' || 
                       computedStyle.transform.includes('translateY(-100%)') ||
                       parseFloat(computedStyle.height) <= 1; // Consider 1px or less as hidden
      
      return !isHidden;
    };
    
    // Try mobile first, then desktop
    if (mobileAnnouncement && checkIfVisible(mobileAnnouncement, 'Mobile')) {
      activeAnnouncementHeight = getElementHeight(mobileAnnouncement);
    } else if (desktopAnnouncement && checkIfVisible(desktopAnnouncement, 'Desktop')) {
      activeAnnouncementHeight = getElementHeight(desktopAnnouncement);
    }
    
    setCSSProperty(config.cssProperties.announcementHeight, `${activeAnnouncementHeight}px`);

    calculateStickyThreshold();
    measureOriginalPositions();
  }

  /**
   * Handle scroll state
   */
  function handleScroll() {
    // Early returns for invalid states
    if (isIgnoringScroll || !$header) return;

    const currentScrollY = window.scrollY;
    const scrollDifference = currentScrollY - lastScrollY;
    
    if (Math.abs(scrollDifference) > 5) {
      isScrollingDown = scrollDifference > 0;
    }

    // Use dynamic threshold based on announcement height for better UX
    const announcementHeight = parseInt(document.documentElement.style.getPropertyValue('--announcement-height')) || config.thresholds.top;
    const dynamicThreshold = Math.max(announcementHeight - 5, config.thresholds.top); // 5px buffer
    
    if (currentScrollY <= dynamicThreshold) {
      $header.classList.remove(config.classes.headerHidden);
      document.body?.classList.add(config.classes.atTop);
      document.body?.classList.remove(config.classes.stickyActive, config.classes.headerHiddenFallback);
      lastScrollY = currentScrollY;
      return;
    }

    document.body?.classList.remove(config.classes.atTop);
    
    const headerHeight = getElementHeight($header);
    const hasPassedOriginalPosition = currentScrollY < (originalNavPosition - headerHeight - 20);
    
    if (hasPassedOriginalPosition && !isScrollingDown) {
      document.body?.classList.remove(config.classes.stickyActive, config.classes.headerHiddenFallback);
      $header.classList.remove(config.classes.headerHidden);
      lastScrollY = currentScrollY;
      return;
    }
    
    const hasPassedCollectionContent = currentScrollY > stickyThreshold;
    let shouldActivateSticky = false;
    
    if (hasPassedCollectionContent) {
      if (Math.abs(currentScrollY - stickyThreshold) < 10) {
        const headerRect = getElementRect($header);
        
        if (isCollectionPage && $collectionNavWrapper) {
          const navRect = getElementRect($collectionNavWrapper);
          shouldActivateSticky = headerRect && navRect && navRect.top <= headerRect.bottom;
        } else if (!isCollectionPage && $searchField) {
          const searchRect = getElementRect($searchField);
          shouldActivateSticky = headerRect && searchRect && searchRect.top <= headerRect.bottom;
        }
      } else {
        shouldActivateSticky = (isCollectionPage && $collectionNavWrapper) || (!isCollectionPage && $searchField);
      }
    }

    if (shouldActivateSticky) {
      document.body?.classList.add(config.classes.stickyActive);
      
      if (isScrollingDown && currentScrollY > (stickyThreshold + headerHeight + config.thresholds.scroll)) {
        $header.classList.add(config.classes.headerHidden);
        document.body?.classList.add(config.classes.headerHiddenFallback);
      } else if (!isScrollingDown) {
        $header.classList.remove(config.classes.headerHidden);
        document.body?.classList.remove(config.classes.headerHiddenFallback);
      }
    } else {
      document.body?.classList.remove(config.classes.stickyActive, config.classes.headerHiddenFallback);
      $header.classList.remove(config.classes.headerHidden);
    }

    lastScrollY = currentScrollY;
  }

  /**
   * Throttled scroll handler for performance
   */
  function throttledScrollHandler() {
    if (ticking) return;
    
    requestAnimationFrame(() => {
      if (!hasUserScrolled) {
        hasUserScrolled = true;
        initializeCatNav();
      }
      handleScroll();
      ticking = false;
    });
    ticking = true;
  }

  /**
   * Initialize cat nav functionality
   */
  function initializeCatNav() {
    if (isInitialized) return;
    
    updateHeights();
    
    resizeHandler = () => {
      if (isInitialized) {
        updateHeights();
      }
    };
    
    window.addEventListener?.('resize', resizeHandler);
    isInitialized = true;
  }

  /**
   * Initialize the navigation manager
   */
  function init() {
    // Fail fast if critical elements are missing
    if (!initializeElements()) {
      return false;
    }

    setupKeyboardDetection();
    document.addEventListener?.('scroll', throttledScrollHandler, { passive: true });
    return true;
  }

  /**
   * Cleanup
   */
  function destroy() {
    window.removeEventListener?.('scroll', throttledScrollHandler);
    if (resizeHandler) {
      window.removeEventListener?.('resize', resizeHandler);
    }
    
    // Reset state
    isInitialized = false;
    hasUserScrolled = false;
    isIgnoringScroll = false;
    isKeyboardOpen = false;
    resizeHandler = null;
  }

  return {
    init,
    destroy,
    config,
    calculateStickyThreshold
  };
})();

// Initialize with basic error handling for critical failures
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    try {
      CatNavigationManager.init();
      // Ensure calculateStickyThreshold runs after DOM is fully loaded
      CatNavigationManager.calculateStickyThreshold();
    } catch (error) {
      console.error('CatNavigationManager: Fatal initialization error:', error);
    }
  });
} else {
  try {
    CatNavigationManager.init();
    // Ensure calculateStickyThreshold runs after DOM is fully loaded
    CatNavigationManager.calculateStickyThreshold();
  } catch (error) {
    console.error('CatNavigationManager: Fatal initialization error:', error);
  }
} 


